import { useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  PlaneTakeoff, PlaneLanding, Trash2, Plus, Sparkles, Moon, Sun, Coffee, Droplet, Plane, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const OFFSETS = [
  { label: "UTC-08 · LAX/SFO", value: -480 },
  { label: "UTC-05 · JFK/EWR", value: -300 },
  { label: "UTC+00 · LHR/LIS", value: 0 },
  { label: "UTC+01 · CDG/FRA", value: 60 },
  { label: "UTC+02 · CAI/ATH", value: 120 },
  { label: "UTC+03 · DXB (-1)", value: 180 },
  { label: "UTC+04 · DXB/AUH", value: 240 },
  { label: "UTC+05:30 · BOM/DEL", value: 330 },
  { label: "UTC+07 · BKK/HAN", value: 420 },
  { label: "UTC+08 · SIN/HKG/PEK", value: 480 },
  { label: "UTC+09 · NRT/ICN", value: 540 },
  { label: "UTC+10 · SYD/MEL", value: 600 },
  { label: "UTC+12 · AKL", value: 720 },
];

function localInputValue(offsetHours = 0) {
  const d = new Date(Date.now() + offsetHours * 3600 * 1000);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const KIND_META = {
  flight:           { label: "Flight",              tint: "text-sky-300",     bg: "bg-sky-500/60",     icon: Plane },
  anchor_sleep:     { label: "Anchor Sleep",        tint: "text-indigo-300",  bg: "bg-indigo-500/70",  icon: Moon },
  pre_adapt_sleep:  { label: "Pre-adapt Sleep",     tint: "text-violet-300",  bg: "bg-violet-500/70",  icon: Moon },
  inflight_nap:     { label: "In-flight Nap",       tint: "text-cyan-300",    bg: "bg-cyan-500/70",    icon: Moon },
  hydration:        { label: "Hydration Window",    tint: "text-sky-200",     bg: "bg-sky-400/50",     icon: Droplet },
  light_exposure:   { label: "Bright Light",        tint: "text-amber-300",   bg: "bg-amber-500/70",   icon: Sun },
};

export default function JetLagMode() {
  const [legs, setLegs] = useState([
    {
      origin: "BOM",
      destination: "LHR",
      depart_iso: localInputValue(72),
      depart_offset: 330,
      arrive_iso: localInputValue(78),
      arrive_offset: 0,
    },
  ]);
  const [preAdapt, setPreAdapt] = useState(3);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const addLeg = () => {
    const last = legs[legs.length - 1];
    setLegs([...legs, {
      origin: last?.destination || "",
      destination: "",
      depart_iso: last?.arrive_iso || localInputValue(96),
      depart_offset: last?.arrive_offset ?? 0,
      arrive_iso: localInputValue(102),
      arrive_offset: 0,
    }]);
  };
  const removeLeg = (i) => setLegs(legs.filter((_, idx) => idx !== i));
  const setLeg = (i, patch) => setLegs(legs.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/jet-lag/plan", { legs, pre_adapt_days: Number(preAdapt) });
      setPlan(data);
      toast.success("Jet lag plan ready", { description: `${data.blocks.length} blocks across ${data.total_tz_crossed_h}h of TZ shift` });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate plan");
    } finally { setBusy(false); }
  };

  return (
    <section className="space-y-6">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <PlaneTakeoff className="w-6 h-6 text-sky-300" /> Jet Lag Mode
        </h2>
        <p className="text-slate-400 text-sm mt-1">
          Multi-leg travel planner for pilots, cabin crew, and long-haul travelers. Pre-adapts your sleep windows across time zones.
        </p>
      </div>

      <div className="circa-card p-6 space-y-4">
        {legs.map((leg, i) => (
          <div key={i} className="rounded-2xl border border-white/10 bg-slate-900/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="pill bg-slate-950/70 border-white/10 text-slate-300">
                <PlaneTakeoff className="w-3 h-3" /> Leg {i + 1}
              </div>
              {legs.length > 1 && (
                <Button size="icon" variant="ghost" onClick={() => removeLeg(i)} data-testid={`jetlag-remove-leg-${i}`} className="text-rose-400 hover:bg-rose-500/10 rounded-full">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Origin (IATA)</Label>
                <Input value={leg.origin} onChange={(e) => setLeg(i, { origin: e.target.value.toUpperCase() })} data-testid={`jetlag-origin-${i}`} placeholder="BOM" className="mt-1 bg-slate-900/60 border-white/10 text-slate-100 font-mono-ui tracking-widest uppercase" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Destination (IATA)</Label>
                <Input value={leg.destination} onChange={(e) => setLeg(i, { destination: e.target.value.toUpperCase() })} data-testid={`jetlag-destination-${i}`} placeholder="LHR" className="mt-1 bg-slate-900/60 border-white/10 text-slate-100 font-mono-ui tracking-widest uppercase" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Depart (local)</Label>
                <Input type="datetime-local" value={leg.depart_iso} onChange={(e) => setLeg(i, { depart_iso: e.target.value })} className="mt-1 bg-slate-900/60 border-white/10 text-slate-100" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Origin timezone</Label>
                <Select value={String(leg.depart_offset)} onValueChange={(v) => setLeg(i, { depart_offset: Number(v) })}>
                  <SelectTrigger className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F141F] border-white/10 text-slate-100 max-h-72">
                    {OFFSETS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Arrive (local)</Label>
                <Input type="datetime-local" value={leg.arrive_iso} onChange={(e) => setLeg(i, { arrive_iso: e.target.value })} className="mt-1 bg-slate-900/60 border-white/10 text-slate-100" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Destination timezone</Label>
                <Select value={String(leg.arrive_offset)} onValueChange={(v) => setLeg(i, { arrive_offset: Number(v) })}>
                  <SelectTrigger className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F141F] border-white/10 text-slate-100 max-h-72">
                    {OFFSETS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}

        <div className="flex flex-wrap gap-3 items-end">
          <Button variant="outline" onClick={addLeg} data-testid="jetlag-add-leg" className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5">
            <Plus className="w-4 h-4 mr-1" /> Add leg
          </Button>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Pre-adapt days</Label>
            <Input
              type="number"
              min={0}
              max={5}
              value={preAdapt}
              onChange={(e) => setPreAdapt(e.target.value)}
              data-testid="jetlag-pre-adapt"
              className="mt-1 w-24 bg-slate-900/60 border-white/10 text-slate-100"
            />
          </div>
          <div className="flex-1" />
          <Button
            onClick={generate}
            disabled={busy}
            data-testid="btn-generate-jetlag"
            className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
          >
            <Sparkles className="w-4 h-4 mr-1" /> {busy ? "Building…" : "Generate plan"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {plan && <PlanReview plan={plan} />}
    </section>
  );
}

function PlanReview({ plan }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <MiniStat label="TZ crossed" value={`${plan.total_tz_crossed_h}h`} tint="text-sky-300" />
        <MiniStat label="Adaptation" value={`${plan.adaptation_days}d`} tint="text-indigo-300" />
        <MiniStat label="Plan blocks" value={plan.blocks.length} tint="text-amber-300" />
      </div>

      <div className="circa-card p-6">
        <div className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
          <PlaneLanding className="w-4 h-4 text-sky-300" /> Your adaptation plan
        </div>
        <div className="space-y-3">
          {plan.blocks.map((b, i) => {
            const m = KIND_META[b.kind] || { label: b.kind, tint: "text-slate-300", bg: "bg-slate-500/50", icon: Coffee };
            const Icon = m.icon;
            return (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg ${m.bg} flex items-center justify-center shrink-0`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-medium ${m.tint}`}>{b.label}</span>
                    <span className="pill text-[9px] bg-slate-900/60 border-white/10 text-slate-400 normal-case tracking-normal font-sans font-normal">{m.label}</span>
                  </div>
                  <div className="text-xs text-slate-400 font-mono-ui mt-0.5">
                    {new Date(b.start).toLocaleString()} → {new Date(b.end).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {plan.notes?.length > 0 && (
        <div className="circa-card p-6">
          <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-500 mb-3">Notes</div>
          <ul className="space-y-2 text-sm text-slate-300">
            {plan.notes.map((n, i) => (
              <li key={i} className="flex gap-2"><span className="text-indigo-400 mt-1">•</span>{n}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tint }) {
  return (
    <div className="circa-card p-5">
      <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-500 mb-2">{label}</div>
      <div className={`font-display text-2xl font-bold ${tint}`}>{value}</div>
    </div>
  );
}
